import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { endOfMonth, format, parseISO, startOfMonth } from 'date-fns';
import { ArrowDownTrayIcon, DocumentChartBarIcon } from '@heroicons/react/24/outline';
import { LoaderSpinner } from '../../components/LoaderSpinner';
import { CardTotal } from '../../components/quickbooks/CardTotal';
import { DatePickerInput } from '../../components/DatePickerInput';
import { MonthPickerInput } from '../../components/MonthPickerInput';
import { SelectDropdown } from '../../components/SelectDropdown';
import ReportTemplate from '../../components/reports/ReportTemplate';
import AmazonProfitLossTable from '../../components/reports/AmazonProfitLossTable';
import { ROUTES } from '../../consts/general';
import { AMAZON_PL_SUMMARY } from '../../consts/reports';
import { getRangeForType } from '../../helpers/profitAndLoss/common';
import useDateRangeParams from '../../hooks/useDateRangeParams';

const PERIOD_OPTIONS = [
  { value: 'this-year-to-date', label: 'This Year to Date' },
  { value: 'last-month', label: 'Last Month' },
  { value: 'last-quarter', label: 'Last Quarter' },
  { value: 'last-year', label: 'Last Year' },
  { value: 'select-month', label: 'Select Month' },
  { value: 'custom-dates', label: 'Custom Dates' },
];
const DEFAULT_RANGE = getRangeForType('this-year-to-date');
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const findRow = (report, sectionKey, rowKey) =>
  report?.sections?.find((section) => section.key === sectionKey)?.rows?.find((row) => row.key === rowKey);

const AmazonPLSummaryReport = ({ marginFromSidebar }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasDateParams = Boolean(searchParams.get('startDate') || searchParams.get('endDate'));
  const initialType = searchParams.get('type') || (hasDateParams ? 'custom-dates' : 'this-year-to-date');
  const { startDate, endDate, setDateRange } = useDateRangeParams(DEFAULT_RANGE.start, DEFAULT_RANGE.end);
  const [type, setType] = useState(initialType);
  const [selectedMonth, setSelectedMonth] = useState(startDate.slice(0, 7));
  const [basis, setBasis] = useState(searchParams.get('basis') || 'posted');
  const [marketplace, setMarketplace] = useState(searchParams.get('marketplace') || 'us');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState(null);

  const updateParams = useCallback((updates) => {
    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') nextParams.delete(key);
        else nextParams.set(key, value);
      });
      return nextParams;
    }, { replace: true });
  }, [setSearchParams]);

  const applyType = useCallback((nextType) => {
    setType(nextType);
    if (nextType === 'select-month') {
      const month = selectedMonth || format(new Date(), 'yyyy-MM');
      setSelectedMonth(month);
      const monthDate = parseISO(`${month}-01`);
      setDateRange({
        startDate: format(startOfMonth(monthDate), 'yyyy-MM-dd'),
        endDate: format(endOfMonth(monthDate), 'yyyy-MM-dd'),
      }, { type: nextType });
      return;
    }
    const range = getRangeForType(nextType);
    if (range) setDateRange({ startDate: range.start, endDate: range.end }, { type: nextType });
  }, [selectedMonth, setDateRange]);

  const handleCustomDateChange = useCallback((nextRange) => {
    setType('custom-dates');
    setDateRange(nextRange, { type: 'custom-dates' });
  }, [setDateRange]);

  const handleMonthChange = useCallback((month) => {
    if (!month) return;
    setSelectedMonth(month);
    setType('select-month');
    const monthDate = parseISO(`${month}-01`);
    setDateRange({
      startDate: format(startOfMonth(monthDate), 'yyyy-MM-dd'),
      endDate: format(endOfMonth(monthDate), 'yyyy-MM-dd'),
    }, { type: 'select-month' });
  }, [setDateRange]);

  const handleBasisChange = (nextBasis) => {
    setBasis(nextBasis);
    updateParams({ basis: nextBasis });
  };

  const handleMarketplaceChange = (nextMarketplace) => {
    setMarketplace(nextMarketplace);
    updateParams({ marketplace: nextMarketplace });
  };

  useEffect(() => {
    if (!searchParams.get('type')) updateParams({ type: initialType });
  }, [initialType, searchParams, updateParams]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ startDate, endDate, basis, marketplace });
      const response = await fetch(`/api/v1/reports/${AMAZON_PL_SUMMARY}?${params}`, { headers: authHeaders() });
      const json = await response.json();
      if (!response.ok) throw new Error(json.error || 'Failed to load report');
      setReport(json);
    } catch (requestError) {
      setError(requestError.message);
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [basis, endDate, marketplace, startDate]);

  useEffect(() => { load(); }, [load]);

  const handleExport = useCallback(async () => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams({ startDate, endDate, basis, marketplace });
      const response = await fetch(`/api/v1/reports/${AMAZON_PL_SUMMARY}/export?${params}`, {
        headers: authHeaders(),
      });
      if (!response.ok) {
        const json = await response.json().catch(() => ({}));
        throw new Error(json.error || 'Failed to export report');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `amazon_pl_summary_${startDate}_to_${endDate}${marketplace === 'us' ? '_us' : ''}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setIsExporting(false);
    }
  }, [basis, endDate, marketplace, startDate]);

  const tooltipFor = (row) => row.description;
  const months = report?.meta?.months || [];
  const visibleSections = useMemo(
    () => (report?.sections || []).filter((section) => section.rows.length > 0),
    [report],
  );

  const cards = useMemo(() => {
    const definitions = [
      ['Total Revenue', 'revenue', 'totalRevenue'],
      ['Total Expenses', 'expenses', 'totalExpenses'],
      ['Net After Amazon Fees', 'net', 'netTotal'],
      ['Amazon Reimbursement', 'revenue', 'amazonReimbursement'],
      ['AMZ Collected Sales Tax', 'salesTax', 'amzCollectedSalesTax'],
      ['AOV', 'aov', 'aov'],
    ];
    return definitions.map(([title, sectionKey, rowKey]) => {
      const row = findRow(report, sectionKey, rowKey);
      const data = { title, amount: row?.ytd ?? null };
      if (row?.percentOfSales !== null && row?.percentOfSales !== undefined) {
        data.percentage = row.percentOfSales * 100;
      }
      return {
        data,
        percentageLabel: row?.percentBasis ? `${row.percentBasis}: ` : undefined,
      };
    });
  }, [report]);

  const filters = (
    <div className="flex flex-row items-center gap-3 flex-wrap text-sm text-zinc-500">
      <div className="flex flex-row items-center gap-2">
        <span className="text-sm text-zinc-500">Reporting Period:</span>
        <div className="w-52">
          <SelectDropdown
            options={PERIOD_OPTIONS}
            value={type}
            onChange={applyType}
            selectClassname="!bg-white !border-gray-300 !rounded-lg !text-stone-900"
          />
        </div>
      </div>
      {type === 'custom-dates' && (
        <>
          <div className="w-52">
            <DatePickerInput
              value={startDate}
              onChange={(value) => handleCustomDateChange({ startDate: value, endDate })}
              placeholder="Start date"
              min="2024-01-01"
              max={endDate || undefined}
              buttonClassName="!rounded-lg !border-gray-300 !bg-white !text-stone-900"
            />
          </div>
          <span>to</span>
          <div className="w-52">
            <DatePickerInput
              value={endDate}
              onChange={(value) => handleCustomDateChange({ startDate, endDate: value })}
              placeholder="End date"
              min={startDate || '2024-01-01'}
              buttonClassName="!rounded-lg !border-gray-300 !bg-white !text-stone-900"
            />
          </div>
        </>
      )}
      {type === 'select-month' && (
        <div className="w-52">
          <MonthPickerInput
            value={selectedMonth}
            onChange={handleMonthChange}
            placeholder="Pick a month"
            min="2024-01"
            max={format(new Date(), 'yyyy-MM')}
            buttonClassName="!rounded-lg !border-gray-300 !bg-white !text-stone-900"
          />
        </div>
      )}
      <div className="inline-flex overflow-hidden rounded-lg border border-gray-300">
        <button
          type="button"
          onClick={() => handleBasisChange('posted')}
          className={`px-4 py-2 text-sm font-semibold ${basis === 'posted' ? 'bg-green-100 text-green-800' : 'bg-white text-zinc-500'}`}
        >
          Posted Basis
        </button>
        <button
          type="button"
          onClick={() => handleBasisChange('release')}
          className={`border-l border-gray-300 px-4 py-2 text-sm font-semibold ${basis === 'release' ? 'bg-green-100 text-green-800' : 'bg-white text-zinc-500'}`}
        >
          Release Basis
        </button>
      </div>
      <div className="inline-flex overflow-hidden rounded-lg border border-gray-300">
        <button
          type="button"
          onClick={() => handleMarketplaceChange('us')}
          className={`px-4 py-2 text-sm font-semibold ${marketplace === 'us' ? 'bg-green-100 text-green-800' : 'bg-white text-zinc-500'}`}
        >
          United States
        </button>
        <button
          type="button"
          onClick={() => handleMarketplaceChange('na')}
          className={`border-l border-gray-300 px-4 py-2 text-sm font-semibold ${marketplace === 'na' ? 'bg-green-100 text-green-800' : 'bg-white text-zinc-500'}`}
        >
          North America
        </button>
      </div>
    </div>
  );

  return (
    <ReportTemplate
      reportId={AMAZON_PL_SUMMARY}
      title="Amazon P&L Summary"
      description="Monthly Amazon P&L rollup by GL account with YTD and % of sales"
      breadcrumbs={[
        { label: 'Reports', icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" /> },
        { label: '' },
      ]}
      onBack={() => navigate(ROUTES.REPORTS)}
      marginFromSidebar={marginFromSidebar}
      isLoading={loading}
      error={error}
      filters={filters}
      exportButton={(
        <button
          type="button"
          onClick={handleExport}
          disabled={isExporting || loading}
          className="flex items-center justify-between gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-stone-900 transition-all duration-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isExporting ? (
            <>
              <LoaderSpinner width="16" height="16" border="2" parentHeight="h-4" />
              <span>Exporting...</span>
            </>
          ) : (
            <>
              <ArrowDownTrayIcon className="h-4 w-4" />
              <span>Export CSV</span>
            </>
          )}
        </button>
      )}
      renderChildrenOnly
    >
      {!report ? (
        <div className="rounded-lg border border-gray-200 bg-white px-6 py-12 text-center text-sm text-gray-500">No data available.</div>
      ) : (
        <>
          <section className="my-4 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6">
            {cards.map(({ data, percentageLabel }) => (
              <CardTotal key={data.title} data={data} percentageLabel={percentageLabel} isLoading={loading} />
            ))}
          </section>
          <div className="space-y-8">
            {visibleSections.map((section) => (
              <AmazonProfitLossTable
                key={section.key}
                section={section}
                months={months}
                tooltipFor={tooltipFor}
              />
            ))}
          </div>
        </>
      )}
    </ReportTemplate>
  );
};

export default AmazonPLSummaryReport;
